import React, { useRef, useState, useEffect } from 'react';
import {
	Chart as ChartJS,
	RadialLinearScale,
	PointElement,
	LineElement,
	Filler,
	Tooltip,
	Legend,
} from 'chart.js';
import { Radar } from 'react-chartjs-2';
import styled from 'styled-components';

const ScoreIndicator = styled.div`
	position: absolute;
	display: flex;
	gap: 4px;
	transform: translate(-50%, -50%);
	z-index: 2;
	font-family: 'Inter', sans-serif;
`;

const Score = styled.div`
	padding: 2px 8px;
	border-radius: 8px;
	font-size: 10px;
	font-weight: 300;
	color: ${(props) => props.color};
	background: ${(props) => props.background};
`;

ChartJS.register(
	RadialLinearScale,
	PointElement,
	LineElement,
	Filler,
	Tooltip,
	Legend,
);

const SpiderChart = ({ data }) => {
	const chartRef = useRef(null);
	const containerRef = useRef(null);
	const [scorePositions, setScorePositions] = useState([]);

	useEffect(() => {
		if (chartRef.current) {
			const chart = chartRef.current;
			const scale = chart.scales.r;
			const chartArea = chart.chartArea;
			const centerX = (chartArea.left + chartArea.right) / 2;
			const centerY = (chartArea.top + chartArea.bottom) / 2;
			const radius = scale.height / 2;

			// Calculate positions for score indicators
			const positions = data.labels.map((_, index) => {
				const angle =
					(index * (360 / data.labels.length) - 90) * (Math.PI / 180);
				// Adjust only top and bottom positions (when angle is close to -90° or 90°)
				const isTopOrBottom = Math.abs(Math.sin(angle)) > 0.95;
				const labelRadius = radius + (isTopOrBottom ? 35 : 50);

				return {
					x: centerX + Math.cos(angle) * labelRadius,
					y: centerY + Math.sin(angle) * labelRadius,
					scores: data.datasets.map((dataset) => dataset.data[index]),
				};
			});

			setScorePositions(positions);
		}
	}, [chartRef.current, data]);

	const chartOptions = {
		...data.options,
		maintainAspectRatio: false,
		responsive: true,
		plugins: {
			...data.options?.plugins,
			legend: {
				display: false,
			},
		},
		scales: {
			r: {
				...data.options?.scales?.r,
				angleLines: {
					...data.options?.scales?.r?.angleLines,
					borderCapStyle: 'round',
					borderJoinStyle: 'round',
				},
				grid: {
					...data.options?.scales?.r?.grid,
					borderCapStyle: 'round',
					borderJoinStyle: 'round',
				},
				pointLabels: {
					font: {
						size: 12,
						weight: '500',
					},
					color: '#333',
					padding: 20,
					callback: function (value, index) {
						// Check if labels have custom format with values
						if (
							data.labelsWithValues &&
							data.labelsWithValues[index]
						) {
							const labelData = data.labelsWithValues[index];
							// Display values above the label with line breaks
							return [
								`${labelData.value1}%`,
								`${labelData.value2}%`,
								`${labelData.value3}%`,
								labelData.label,
							];
						}
						return value;
					},
				},
			},
		},
		elements: {
			...data.options?.elements,
			point: {
				...data.options?.elements?.point,
				radius: 4,
				hoverRadius: 6,
				borderWidth: 2,
				hoverBorderWidth: 2,
				backgroundColor: 'red',
			},
			line: {
				...data.options?.elements?.line,
				tension: 0.2,
			},
		},
	};

	return (
		<div
			ref={containerRef}
			style={{
				width: '100%',
				height: '100%',
				minHeight: '300px',
				position: 'relative',
			}}
		>
			<Radar
				ref={chartRef}
				data={{
					labels: data.labels,
					datasets: data.datasets,
				}}
				options={chartOptions}
			/>
			{scorePositions.map((position, index) => (
				<ScoreIndicator
					key={index}
					style={{
						left: position.x,
						top: position.y,
					}}
				>
					{position.scores.map((score, scoreIndex) => (
						<Score
							key={scoreIndex}
							color={data.datasets[scoreIndex]?.borderColor}
							background={`${data.datasets[scoreIndex]?.borderColor}26`}
						>
							{score}
						</Score>
					))}
				</ScoreIndicator>
			))}
		</div>
	);
};

export default SpiderChart;
